import { createTheme, ThemeOptions } from '@mui/material/styles';

let theme = createTheme({
  constants: {
    sidebarElementSize: 44,
    sidebarButtonWidth: 28,
    headerButtonSize: 30,
    modalContentGap: 20,
    formGap: 12,
  },
  palette: {
    primary: {
      main: '#2b6ff2',
    },
    secondary: {
      main: '#f4f6f8',
      dark: '#f0f0f0',
    },
    success: {
      main: '#56AC4A',
    },
    warning: {
      main: '#CE1D1D',
    },
    info: {
      main: '#2b6ff2',
    },
    black: { main: '#111111', contrastText: '#ffffff' },
  },
  typography: {
    fontFamily: 'Inter',
    fontSize: 12,
    button: {
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 4,
  },
  spacing: 4,
});

theme = createTheme(theme, {
  components: {
    MuiTypography: {
      variants: [
        {
          props: { variant: 'header' },
          style: {
            color: 'var(--text-heading-color)',
            fontWeight: 500,
            fontSize: 22,
            letterSpacing: -0.2,
            lineHeight: 'normal',
          },
        },
        {
          props: { variant: 'subtitle1' },
          style: {
            color: 'var(--color-neutral-900)',
            fontWeight: 600,
            fontSize: 12,
          },
        },
        {
          props: { variant: 'subtitle2' },
          style: {
            color: 'var(--color-neutral-400)',
            fontWeight: 400,
            fontSize: 12,
          },
        },
        {
          props: { variant: 'body1' },
          style: {
            fontWeight: 400,
            fontSize: 12,
          },
        },
        {
          props: { variant: 'body2' },
          style: {
            fontWeight: 400,
            fontSize: 12,
          },
        },
        {
          props: { variant: 'h2' },
          style: {
            fontSize: 12,
          },
        },
      ],
    },
    MuiList: {
      styleOverrides: {},
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          lineHeight: 'normal',
          letterSpacing: -0.1,
          padding: '10px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 400,
          gap: 8,
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          color: 'var(--button-button-primary-text-color)',
          svg: {
            fill: 'var(--button-button-primary-text-color)',
          },
          border: '1px solid var(--button-button-primary-stroke-color)',
        },
        basicPrimary: {
          color: 'var(--info-info-text-primary-color)',
          background: '#DDE7F9',
          svg: {
            fill: 'var(--button-button-primary-color)',
          },
          border: '1px solid var(--button-button-primary-stroke-color)',
        },
        containedSecondary: {
          color: 'var(--button-button-secondary-text-color)',
          border: '1px solid var(--button-button-secondary-stroke-color)',
          svg: {
            fill: 'var(--color-neutral-500)',
          },
        },
        textSecondary: {
          color: 'var(--button-button-secondary-text-color)',
          svg: {
            fill: 'var(--color-neutral-500)',
          },
        },
        sizeLarge: {
          padding: '10px 24px',
          borderRadius: 8,
          fontSize: 13,
        },
        sizeMedium: {
          padding: '10px 24px',
          borderRadius: 8,
          fontSize: 13,
        },
        sizeSmall: {
          padding: '6px 10px',
          borderRadius: 4,
          fontSize: 12,
          svg: {
            maxWidth: 14,
            maxHeight: 14,
          },
        },
        action: {
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          '&:hover': {
            background: 'rgba(255,255,255,0.5)',
          },
        },
        actionError: {
          background: '#fff',
          border: '1px solid var(--error-error-stroke-color)',
          color: 'var(--error-error-text-primary-color)',
          svg: {
            fill: 'var(--error-error-icon-primary-color)',
          },
        },
        actionInfo: {
          background: '#fff',
          border: '1px solid var(--info-info-stroke-color)',
          color: 'var(--info-info-text-primary-color)',
          svg: {
            fill: 'var(--info-info-icon-primary-color)',
          },
        },
        actionSuccess: {
          background: '#fff',
          border: '1px solid var(--success-success-stroke-color)',
          color: 'var(--success-success-text-primary-color)',
          svg: {
            fill: 'var(--success-success-icon-primary-color)',
          },
        },
        actionWarning: {
          background: '#fff',
          border: '1px solid var(--warning-warning-stroke-color)',
          color: 'var(--warning-warning-text-primary-color)',
          svg: {
            fill: 'var(--warning-warning-icon-primary-color)',
          },
        },
        ctaPrimary: {
          backgroundColor: '#F28511',
          border: '1px solid var(--button-button-primary-stroke-color)',
          padding: `${theme.spacing(3)} ${theme.spacing(5)}`,
          fontWeight: 600,
          fontSize: 14,
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          color: 'var(--button-button-primary-text-color)',
          borderRadius: theme.spacing(1),
          '&:hover': {
            backgroundColor: '#FFA531',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: 0,
          borderRadius: 4,
          overflow: 'hidden',
          svg: {
            fill: 'var(--color-neutral-500)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          padding: '3px 8px',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: 'Inter',
          opacity: 1,
          backgroundColor: 'var(--tooltip-tooltip-background-color)',
          color: 'var(--button-button-primary-text-color)',
          fontWeight: 500,
          fontSize: 12,
          lineHeight: '14px',
          padding: '8px 12px',
          svg: {
            fill: 'var(--color-white)',
          },
        },
        arrow: {
          opacity: 1,
          color: 'var(--tooltip-tooltip-background-color)',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: theme.palette.black.main,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          boxSizing: 'border-box',
        },
        root: {
          boxSizing: 'border-box',
          fieldset: {
            border: 'none',
          },
        },
      },
    },
    MuiSelect: {
      variants: [
        {
          props: { size: 'small' },

          style: {
            '& .MuiSelect-select': {
              height: '24px !important',
              fontSize: 11,
              padding: '5px 10px',
            },
          },
        },
        {
          props: { size: 'medium' },

          style: {
            '& .MuiSelect-select': {
              height: '36px !important',
              fontSize: 13,
              padding: 12,
            },
          },
        },
        {
          props: { size: 'large' },

          style: {
            '& .MuiSelect-select': {
              height: '48px !important',
              fontSize: 13,
              padding: '16.5px 14px',
            },
          },
        },
      ],
      styleOverrides: {
        filled: {
          backgroundColor: 'var(--input-input-background-color)',

          borderRadius: 8,
        },
        outlined: {
          border: '1px solid var(--stroke-stroke-primary-color)',

          borderRadius: 8,
        },
        root: {
          display: 'flex',

          fieldset: {
            display: 'none',
          },
          svg: {
            fill: 'var(--icon-icon-secondary-color)',
          },
          '.MuiSelect-select': {
            height: '36px !important',
            fontSize: 13,
            padding: 12,
            color: theme.palette.black.main,
            fontWeight: 500,
            lineHeight: '14px',
            width: '100%',

            display: 'flex',
            alignItems: 'center',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: 0,
          '.MuiMenuItem-root': {
            fontSize: 13,
            padding: 8,
            lineHeight: 1.2,
            minHeight: 'auto',
            borderRadius: 4,
            display: 'flex',
            gap: 4,
            alignItems: 'center',
            marginTop: 2,
            marginBottom: 2,
            '&:hover': {
              background: 'var(--background-background-secondary-color)',
            },
            '&.Mui-selected': {
              background: 'var(--background-background-secondary-color)',
              fontWeight: 500,
              svg: {
                fill: 'var(--info-info-icon-primary-color)',
              },
            },
          },
        },

        paper: {
          borderRadius: 8,
          padding: 8,
          margin: 0,
          marginTop: 8,
          boxShadow: 'none',
          border: '1px solid var(--stroke-stroke-primary-color)',
          background: 'var(--background-background-color)',

          svg: {
            fill: 'var(--icon-icon-tertiary-color)',
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&::after': {
            display: 'none',
          },
          '&::before': {
            display: 'none',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '.MuiInputBase-root': {
            borderRadius: 8,
            border: 'none',
            backgroundColor: 'var(--input-input-background-color)',
            '&:focus': {
              backgroundColor: 'var(--color-general-grey-100)',
            },
            '&:disabled': {
              backgroundColor: 'var(--color-neutral-300)',
            },
            padding: 0,
            '.MuiAutocomplete-input': {
              padding: '8px 12px',
              background: '#fff',
              border: '1px solid var(--stroke-stroke-primary-color)',
            },
            '.MuiAutocomplete-input[aria-expanded="true"]': {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderBottom: 'none',
            },
          },
          input: {
            borderRadius: 8,
            padding: '8px 12px',
            color: 'var(--text-text-primary-color)',
            backgroundColor: 'var(--input-input-background-color)',
            '&:focus': {
              backgroundColor: 'var(--color-general-grey-100)',
            },
            '&:disabled': {
              backgroundColor: 'var(--color-neutral-300)',
            },
            '&.MuiInputBase-inputAdornedEnd': {
              paddingRight: 32,
            },
            '&::placeholder': {
              color: 'var(--input-placeholder-color)',
              opacity: 0.4,
            },
            fontSize: 13,
            boxSizing: 'border-box',
            height: 36,
          },
          textarea: {
            borderRadius: 8,
            padding: '8px 12px',
            color: 'var(--text-text-primary-color)',
            backgroundColor: 'var(--input-input-background-color)',
            '&:focus': {
              backgroundColor: 'var(--color-general-grey-100)',
            },
            '&:disabled': {
              backgroundColor: 'var(--color-neutral-300)',
            },
            '&::placeholder': {
              color: 'var(--input-placeholder-color)',
              opacity: 0.4,
            },
            fontSize: 13,
            resize: 'vertical',
            minHeight: '6em',
            maxHeight: '15em',
          },
        },

        fieldset: {
          border: 'none',
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontSize: 13,
          fontWeight: 600,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '2px 12px',
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box',
          width: '100%',
          '.MuiAlert-message': {
            width: '100%',
            textAlign: 'left',
          },
          '.MuiAlert-action': {
            height: '100%',
            alignSelf: 'flex-start',
            padding: '4px 0 4px 16px',
          },
        },

        standardError: {
          background: 'var(--error-error-background-color)',
          border: '1px solid var(--error-error-stroke-color)',
          color: 'var(--error-error-text-primary-color)',
          svg: {
            fill: 'var(--error-error-icon-primary-color)',
          },
        },
        standardInfo: {
          background: 'var(--info-info-background-color)',
          border: '1px solid var(--info-info-stroke-color)',
          color: 'var(--info-info-text-primary-color)',
          svg: {
            fill: 'var(--info-info-icon-primary-color)',
          },
        },
        standardSuccess: {
          background: 'var(--success-success-background-color)',
          border: '1px solid var(--success-success-stroke-color)',
          color: 'var(--success-success-text-primary-color)',
          svg: {
            fill: 'var(--success-success-icon-primary-color)',
          },
        },
        standardWarning: {
          background: 'var(--warning-warning-background-color)',
          border: '1px solid var(--warning-warning-stroke-color)',
          color: 'var(--warning-warning-text-primary-color)',
          svg: {
            fill: 'var(--warning-warning-icon-primary-color)',
          },
        },
        compactError: {
          background: 'var(--error-error-background-color)',
          border: '1px solid var(--error-error-stroke-color)',
          color: 'var(--error-error-text-primary-color)',
          svg: {
            fill: 'var(--error-error-icon-primary-color)',
          },
          fontWeight: 400,
          lineHeight: 1.2,
          fontSize: 12,
          padding: '0px 10px',
          borderRadius: 4,
          '.MuiAlert-icon': {
            marginRight: 8,
          },
        },
        compactInfo: {
          background: 'var(--info-info-background-color)',
          border: '1px solid var(--info-info-stroke-color)',
          color: 'var(--info-info-text-primary-color)',
          svg: {
            fill: 'var(--info-info-icon-primary-color)',
          },
          fontWeight: 400,
          lineHeight: 1.2,
          fontSize: 12,
          padding: '0px 10px',
          borderRadius: 4,
          '.MuiAlert-icon': {
            marginRight: 8,
          },
        },
        compactSuccess: {
          background: 'var(--success-success-background-color)',
          border: '1px solid var(--success-success-stroke-color)',
          color: 'var(--success-success-text-primary-color)',
          svg: {
            fill: 'var(--success-success-icon-primary-color)',
          },
          fontWeight: 400,
          lineHeight: 1.2,
          fontSize: 12,
          padding: '0px 10px',
          borderRadius: 4,
          '.MuiAlert-icon': {
            marginRight: 8,
          },
        },
        compactWarning: {
          background: 'var(--warning-warning-background-color)',
          border: '1px solid var(--warning-warning-stroke-color)',
          color: 'var(--warning-warning-text-primary-color)',
          svg: {
            fill: 'var(--warning-warning-icon-primary-color)',
          },
          fontWeight: 400,
          lineHeight: 1.2,
          fontSize: 12,
          padding: '0px 10px',
          borderRadius: 4,
          '.MuiAlert-icon': {
            marginRight: 8,
          },
        },
        icon: {
          alignItems: 'center',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: -8,
          marginTop: -8,
          marginBottom: -8,
          '&.MuiFormControlLabel-labelPlacementStart': {
            marginLeft: 0,
            marginRight: -8,
          },
        },
      },
    },
  },
} as ThemeOptions);

export { theme };
